import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Avatar,
  Divider,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Container,
  Grid,
  Button,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { styled } from '@mui/material/styles';
import MFASetup from '../components/mfa/MFASetup';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';

const SidebarItem = styled(ListItem)(({ theme }) => ({
  padding: '12px 16px',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    cursor: 'pointer',
  },
}));

const SidebarItemActive = styled(ListItem)(({ theme }) => ({
  padding: '12px 16px',
  borderRadius: '4px',
  backgroundColor: 'rgba(0, 0, 0, 0.04)',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    cursor: 'pointer',
  },
}));

const Profile: React.FC = () => {
  const { authState } = useAuth();
  const user = authState.user;
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('Profile');
  const isSecurityOnly = activeSection === 'Security';

  // Handle navigation state from other components
  useEffect(() => {
    if (location.state?.activeSection) {
      setActiveSection(location.state.activeSection);
      return;
    }
    // Support direct routing: /profile/security
    if (
      typeof location.pathname === 'string' &&
      location.pathname.startsWith('/profile/security')
    ) {
      setActiveSection('Security');
    }
  }, [location.state, location.pathname]);

  if (!user) {
    return (
      <div className="p-6 text-center">
        <Typography variant="h5" className="text-gray-700">
          Please sign in to view your profile
        </Typography>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeSection) {
      case 'Profile':
        return (
          <div className="space-y-6">
            {/* Settings Card */}
            <Card className="border border-gray-200 rounded-lg shadow-sm">
              <CardContent className="p-6">
                <Typography variant="h6" className="text-gray-900 mb-1">
                  Settings
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  Name, email, password, address, company, number
                </Typography>
              </CardContent>
            </Card>

            {/* My Company Card */}
            <Card className="border border-gray-200 rounded-lg shadow-sm">
              <CardContent className="p-6">
                <Typography variant="h6" className="text-gray-900 mb-1">
                  My Company
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  Legal Entity Details, VAT/Tax ID, Data Protection Officer contact
                </Typography>
              </CardContent>
            </Card>

            {/* Other Card */}
            <Card className="border border-gray-200 rounded-lg shadow-sm">
              <CardContent className="p-6">
                <Typography variant="h6" className="text-gray-900 mb-1">
                  "Other"
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  Compliance & Data Policy Subsection, Verification & Reminders, Contextual Requests
                </Typography>
              </CardContent>
            </Card>

            {/* Security Card */}
            <Card className="border border-gray-200 rounded-lg shadow-sm">
              <CardContent className="p-6">
                <Typography variant="h6" className="text-gray-900 mb-1">
                  Security
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  2FA, 3FA, encryption
                </Typography>
              </CardContent>
            </Card>
          </div>
        );

      case 'Security':
        return <MFASetup />;

      case 'My Plan':
        return (
          <div className="space-y-6">
            <Card className="border border-gray-200 rounded-lg shadow-sm">
              <CardContent className="p-6">
                <Typography variant="h6" className="text-gray-900 mb-1">
                  My Plan
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  Current subscription plan and features
                </Typography>
              </CardContent>
            </Card>
          </div>
        );

      case 'Billing':
        return (
          <div className="space-y-6">
            <Card className="border border-gray-200 rounded-lg shadow-sm">
              <CardContent className="p-6">
                <Typography variant="h6" className="text-gray-900 mb-1">
                  Billing
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  Payment methods, invoices, and billing history
                </Typography>
              </CardContent>
            </Card>
          </div>
        );

      case 'Wallet':
        return (
          <div className="space-y-6">
            <Card className="border border-gray-200 rounded-lg shadow-sm">
              <CardContent className="p-6">
                <Typography variant="h6" className="text-gray-900 mb-1">
                  Wallet
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  Digital wallet and cryptocurrency management
                </Typography>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return (
          <div className="space-y-6">
            <Card className="border border-gray-200 rounded-lg shadow-sm">
              <CardContent className="p-6">
                <Typography variant="h6" className="text-gray-900 mb-1">
                  Welcome to your Profile
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  Select a section from the sidebar to get started
                </Typography>
              </CardContent>
            </Card>
          </div>
        );
    }
  };

  return (
    <div className="flex h-screen">
      {/* Sidebar (hidden on Security-only view) */}
      {!isSecurityOnly && (
        <div className="w-48 border-r border-gray-200 bg-white">
          <div className="p-4 border-b border-gray-200">
            <Typography variant="h6" className="text-gray-900">
              Profile
            </Typography>
          </div>
          <List component="nav" aria-label="profile navigation" disablePadding>
            <SidebarItem
              onClick={() => setActiveSection('Profile')}
              className={activeSection === 'Profile' ? 'bg-gray-100' : ''}
            >
              <ListItemText primary="Profile" />
            </SidebarItem>
            <SidebarItem
              onClick={() => setActiveSection('My Plan')}
              className={activeSection === 'My Plan' ? 'bg-gray-100' : ''}
            >
              <ListItemText primary="My Plan" />
            </SidebarItem>
            <SidebarItem
              onClick={() => setActiveSection('Billing')}
              className={activeSection === 'Billing' ? 'bg-gray-100' : ''}
            >
              <ListItemText primary="Billing" />
            </SidebarItem>
            <SidebarItem
              onClick={() => setActiveSection('Wallet')}
              className={activeSection === 'Wallet' ? 'bg-gray-100' : ''}
            >
              <ListItemText primary="Wallet" />
            </SidebarItem>
            <SidebarItem
              onClick={() => setActiveSection('Security')}
              className={activeSection === 'Security' ? 'bg-gray-100' : ''}
            >
              <ListItemText primary="Security" />
            </SidebarItem>
          </List>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 p-8 bg-gray-50">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="text"
            startIcon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>}
            onClick={() => navigate('/')}
            sx={{ mb: 2, color: '#617BFF', textTransform: 'none', fontWeight: 500, border: '1px solid #617BFF', borderRadius: '8px', px: 2, '&:hover': { bgcolor: '#F5F7FF' } }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h5" className="text-gray-900">
            {activeSection}
          </Typography>
        </div>
        {/* Security-only view shows just MFA setup */}
        {isSecurityOnly ? <MFASetup /> : renderContent()}
      </div>
    </div>
  );
};

export default Profile;
